<template>
  <div class="accordion" :class="isPlain ? 'accordion--plain' : 'mb-8'">
    <div
      v-for="(section, index) in filteredCategories"
      :key="index"
      :class="{ 'mb-2': !isPlain }"
    >
      <button
        class="transition-color flex w-full items-center justify-between text-left md:px-0"
        :class="isPlain ? 'border-b border-black-10 py-3' : 'bg-platin p-4'"
        @click="toggleCategory(index)"
      >
        <span
          :class="isPlain ? 'text-base font-bold' : 'text-lg font-medium'"
          >{{ section.title }}</span
        >
        <IconArrowDown
          class="size-4 shrink-0 transition-transform duration-150"
          :class="{ '-rotate-180': section.isOpen }"
        />
      </button>
      <transition
        enter-active-class="transition-all duration-150 ease-out"
        leave-active-class="transition-all duration-150 ease-in"
        @enter="onAccordionOpen"
        @leave="onAccordionClose"
      >
        <div
          v-show="section.isOpen"
          class="accordion-panel overflow-hidden bg-white"
          :class="isPlain ? 'pb-2' : 'px-4'"
        >
          <component
            :is="section.component"
            :product="product"
            :product-order-number="productOrderNumber"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, markRaw, provide, toRef } from 'vue'
import { useI18n } from 'vue-i18n'
import type { PropType } from 'vue'
import { getFirstAttributeValue, type Product } from '@scayle/storefront-nuxt'
import AccordionDescription from './accordion/AccordionDescription.vue'
import AccordionDetailsExtras from './accordion/AccordionDetailsExtras.vue'
import AccordionCreditorDetails from './accordion/AccordionCreditorDetails.vue'
import AccordionMaterialsCare from '#layers/base/components/product/productDetailsContent/accordion/AccordionMaterialsCare.vue'
import {
  getAdvancedAttributeFirstValue,
  getProductDescription,
} from '#layers/base/utils/advancedAttribute'

const props = defineProps({
  product: {
    type: Object as PropType<Product | null>,
    default: () => null,
  },
  productOrderNumber: {
    type: String as PropType<string | undefined>,
    default: undefined,
  },
  // 'bars' keeps the grey bg-platin headers (mobile, default).
  // 'plain' renders Figma-style divider rows with 14px text (desktop right column).
  variant: {
    type: String as PropType<'bars' | 'plain'>,
    default: 'bars',
  },
})

const isPlain = computed(() => props.variant === 'plain')

// Let content sub-components (e.g. AccordionDescription) adapt their layout to
// the desktop/mobile variant without prop drilling through <component :is>.
provide('productDetailsVariant', toRef(props, 'variant'))

const i18n = useI18n()

const categories = ref([
  {
    title: i18n.t('pdp.product_details_description'),
    component: markRaw(AccordionDescription),
    isOpen: true,
    hasContent: !!getProductDescription(props.product),
  },
  {
    title: i18n.t('pdp.product_details_material_care'),
    component: markRaw(AccordionMaterialsCare),
    isOpen: false,
    hasContent:
      !!getAdvancedAttributeFirstValue(props.product, 'material') ||
      !!getFirstAttributeValue(props.product?.attributes, 'washComment')?.label,
  },
  // TODO: Add size and fit section back in once the data becomes available
  // {
  //   title: i18n.t('pdp.product_details_size_fit'),
  //   component: AccordionSizeFit,
  //   isOpen: false,
  //   hasContent: false,
  // },
  {
    title: i18n.t('pdp.product_details_details_extras'),
    component: markRaw(AccordionDetailsExtras),
    isOpen: false,
    hasContent: !!props.product?.attributes?.articleFeatureIcon?.values,
  },
  {
    title: i18n.t('pdp.product_creditor_details'),
    component: markRaw(AccordionCreditorDetails),
    isOpen: false,
    hasContent: !!(
      getAdvancedAttributeFirstValue(props.product, 'creditorName') ||
      getAdvancedAttributeFirstValue(props.product, 'creditorAdress') ||
      getAdvancedAttributeFirstValue(props.product, 'creditorPostCode') ||
      getAdvancedAttributeFirstValue(props.product, 'creditorCity') ||
      getAdvancedAttributeFirstValue(props.product, 'creditorCountry') ||
      getAdvancedAttributeFirstValue(props.product, 'creditorWeb')
    ),
  },
])

// Both variants render `categories` in its declared order: Description,
// Material & Care, Details & Extras, Creditor. ECOM-2773 requires desktop and
// mobile to match, so the mobile bars variant adopted what was previously the
// plain-only Figma order rather than keeping its own.
const filteredCategories = computed(() =>
  categories.value.filter((category) => category.hasContent),
)

const toggleCategory = (index: number) => {
  const category = filteredCategories.value[index]
  if (category) {
    category.isOpen = !category.isOpen
  }
}

const onAccordionOpen = (element: Element) => {
  const el = element as HTMLElement
  el.style.height = '0px'

  // Forces browser reflow; reading offsetHeight has the side-effect, void discards it.
  // eslint-disable-next-line sonarjs/void-use
  void el.offsetHeight

  el.style.height = `${el.scrollHeight}px`

  // We must wait for the transition to end before setting the height to auto
  el.addEventListener(
    'transitionend',
    () => {
      if (el.style.height !== '0px') {
        el.style.height = 'auto'
      }
    },
    { once: true },
  )
}

const onAccordionClose = (element: Element) => {
  const el = element as HTMLElement
  el.style.height = `${el.scrollHeight}px`

  // Forces browser reflow; reading offsetHeight has the side-effect, void discards it.
  // eslint-disable-next-line sonarjs/void-use
  void el.offsetHeight

  el.style.height = '0px'
}
</script>

<style scoped lang="scss">
/*
 * Plain (desktop) variant: ECOM-2679 requires 14px for headline, content and
 * selling points. The content sub-components (incl. the shared base
 * AccordionMaterialsCare) carry their own responsive md:text-lg classes, so we
 * normalise font size at the panel level rather than editing each component.
 */
.accordion--plain :deep(.accordion-panel),
.accordion--plain :deep(.accordion-panel *:not(svg):not(svg *)) {
  font-size: 0.875rem !important;
  line-height: 1.45 !important;
}

/*
 * Bars (mobile) variant: ECOM-2773 standardises panel text on 16px, the size
 * the `Material` sub-heading already rendered at, replacing the mix of 12px
 * (text-sm), 14px (text-base) and 16px (`text-md`, which is not in our font
 * scale and so falls through to the inherited size).
 */
.accordion:not(.accordion--plain) :deep(.accordion-panel),
.accordion:not(.accordion--plain)
  :deep(.accordion-panel *:not(svg):not(svg *)) {
  font-size: 1rem !important;
  line-height: 1.5 !important;
}
</style>
